import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import qs from 'qs';

import { Paper, Grid, Typography, TextField, Chip, IconButton, CircularProgress } from '@material-ui/core';
import { Close, EventBusy, Menu, Map as MapIcon } from '@material-ui/icons';
import { useFormik } from 'formik';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Loader from 'components/Loader';
import Modal from 'components/Modal';
import Button from 'components/Button';
import Dropdown from 'components/Dropdown';
import Select from 'components/Select';
import SchedulingMobileMenu from 'components/SchedulingMobileMenu';
import SchedulingAddMobile from 'components/SchedulingAddMobile';
import SchedulingEvent from 'components/SchedulingEvent';
import SchedulingServicesView from 'components/SchedulingServicesView';
import SchedulingOthersView from 'components/SchedulingOthersView';
import SchedulingMapEventView from 'components/SchedulingMapEventView';
import MapMarkerWithInfoWindow from 'components/MapMarkerWithInfoWindow';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { APIProvider, Map } from '@vis.gl/react-google-maps';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFilter } from '@fortawesome/free-solid-svg-icons';

import { setCalendarOption, setIsFirstSchedulingAccess, setDateCycleText, setCurrentDateCycle } from '../store/actions';
import { loadFilters } from 'components/Filter/store/thunk';
import { loadBusinessInfo } from 'pages/BusinessInfo/store/thunk';
import { loadCalendarServices, loadMapServices } from '../store/thunk';
import { loadServicesCategories } from 'pages/Services/store/thunk';
import { persistQueryParamsSearch } from 'components/Filter/store/actions';

import { capitalizeString, formatServicesCategories } from 'helpers/formatters';
import useDidMount from 'helpers/hooks/useDidMount';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

import { situationOptions, appointmentTypeOptions } from './resources';

import { useAutoCompleteStyles } from './styles';

const IconMapPin = require('../../../assets/img/icon_map_pin.svg');

function renderEventContent (eventInfo) {
  const { title } = eventInfo.event._def;

  const {
    quantity,
    startDateTime,
    endDateTime,
    type,
    typeId,
    services,
    isDelivery,
    situationId,
    plate,
    client
  } = eventInfo.event._def.extendedProps;

  const time = `${moment(startDateTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')} - ${moment(endDateTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')}`;

  return(
    <SchedulingEvent
      title={(+typeId == 1) ? services[0]?.serviceName : title }
      quantity={quantity || services?.length}
      time={time}
      startDateTime={startDateTime}
      endDateTime={endDateTime}
      type={type}
      isDelivery={isDelivery}
      situationId={situationId}
      plate={plate}
      street={client?.street}
      phone={(!!client?.phones && JSON.parse(client?.phones)?.length > 0) ? client?.phones[0] : ''}
    />
  );
}

export default function Scheduling() {
  const dispatch = useDispatch();

  const history = useHistory();

  const autoCompleteStyles = useAutoCompleteStyles();

  const { width } = useWindowDimensions();
  const isSm = width < 1150;

  const didComponentMount = useDidMount();

  const calendarRef = useRef(null);

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { selects, qp } = useSelector(state => state.filters);
  const { startDate, endDate } = useSelector(state => state.dateFilter);
  const { establishmentName, street, number, district, postalCode, city, state, isLoading: isEstablishmentLoading } = useSelector(state => state.businessInfo.submit);
  const { servicesCategories } = useSelector(state => state.services);
  const { isLoading, calendarList, isFirstSchedulingAccess, mapList, isCalendarSelected, isMapSelected, dateCycleCurrentState } = useSelector(state => state.scheduling);
  const selectedSchedulingOption = useSelector(state => state.scheduling.selectedCalendarOption);

  const isMonthView = selectedSchedulingOption == 'dayGridMonth';
  const isWeekView = selectedSchedulingOption == 'timeGridWeek';
  const isDayView = selectedSchedulingOption == 'timeGridDay';

  const [isCalendar, setIsCalendar] = useState(true);
  const [isMap, setIsMap] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isSchedulingMenuOpen, setIsSchedulingMenuOpen] = useState(false);
  const [isNoneSchedulingModalOpen, setIsNoneSchedulingModalOpen] = useState(true);
  const [activeMapListId, setActiveMapListId] = useState('');

  const [isAppointmentServicesDrawerOpen, setIsAppointmentServicesDrawerOpen] = useState(false);
  const [isAppointmentOthersDrawerOpen, setIsAppointmentOthersDrawerOpen] = useState(false);

  const [appointmentOthersId, setAppointmentOthersId] = useState('');
  const [appointmentServicesId, setAppointmentServicesId] = useState('');

  const [userLocation, setUserLocation] = useState({
    lat: 0,
    lng: 0
  });

  const [mapDefaultLocation, setMapDefaultLocation] = useState({
    lat: 0,
    lng: 0
  });

  const [establishmentLocation, setEstablishmentLocation] = useState({
    lat: 0,
    lng: 0
  });

  const [dateCycle, setDateCycle] = useState(moment(moment.now()));

  const year = moment(dateCycle, 'YYYY-MM-DD').year();
  const month = moment().month(moment(dateCycle, 'YYYY-MM-DD').month()).format('MM');
  const maxDay = moment(dateCycle, 'YYYY-MM-DD').daysInMonth();

  const initialDate = `${year}-${month}-01`;
  const finalDate = `${year}-${month}-${maxDay + 1}`;

  const handleCalendarRequest = () => {
    const { start, end } = isMonthView ? getCalendarValidMonthRange() : getCalendarValidRange();

    const extraProps = {
      initialDate: start,
      finalDate: isMonthView ? `${year}-${month}-${maxDay}` : end
    }

    dispatch(loadCalendarServices(userId, establishmentId, extraProps, qp));
  }

  const handleMapRequest = () => {
    dispatch(loadMapServices(userId, establishmentId, startDate, endDate, qp ? qp : '&typeId=1'));
  }

  const getAddressLatLng = async address => {
    if(!address) {
      return [];
    }

    await window.google.maps.importLibrary('geocoding');

    const { geocode } = new window.google.maps.Geocoder();

    let results = [];
    let status = 'ZERO_RESULTS';

    await geocode({ address })
      .then(data => {
        results = data?.results;
        status = 'OK';
      })
      .catch(() => {
        results = [];
        status = 'ZERO_RESULTS'
      });

    if(status == 'ZERO_RESULTS') {
      return [];
    }

    if(status == 'OK') {
      return [
        results[0].geometry.location.lat(),
        results[0].geometry.location.lng(),
        results[0]?.address_components
      ];
    }

    return [];
  }

  useEffect(() => {
    setIsCalendar(isCalendarSelected);
  }, [isCalendarSelected]);

  useEffect(() => {
    setIsMap(isMapSelected);
  }, [isMapSelected]);

  useEffect(() => {
    if(isDayView) {
      dispatch(setDateCycleText(dateCycle.format('DD/MMM')));
    }

    if(isWeekView) {
      dispatch(setDateCycleText(`${dateCycle.startOf('week').format('DD/MMM')} - ${dateCycle.endOf('week').format('DD/MMM')}`));
    }

    if(isMonthView) {
      dispatch(setDateCycleText(`${capitalizeString(dateCycle.format('MMMM'))}`));
    }
  }, [dateCycle, selectedSchedulingOption]);

  useEffect(() => {
    if(!dateCycleCurrentState) {
      return;
    }

    if(!didComponentMount) {
      handleDateCycleChange(dateCycleCurrentState);
      dispatch(setCurrentDateCycle(''));
    }
  }, [dateCycleCurrentState]);

  useEffect(() => {
    if(isSm && isFirstSchedulingAccess) {
      dispatch(setIsFirstSchedulingAccess());
      dispatch(setCalendarOption('timeGridWeek'));
    }
  }, [isSm]);

  useEffect(() => {
    if(isDayView) {
      setDateCycle(moment(moment.now()));
    }
  }, [isDayView]);

  useEffect(() => {
    dispatch(persistQueryParamsSearch(''));
  }, []);

  useEffect(() => {
    dispatch(loadFilters(userId, establishmentId));
    dispatch(loadServicesCategories(userId));
    dispatch(loadBusinessInfo({ userId, establishmentId }));

    if(isCalendar && !didComponentMount) {
      handleCalendarRequest();
    }

    if(isMap && !didComponentMount) {
      handleMapRequest();
    }
  }, [isCalendar, isMap, establishmentId]);

  useEffect(() => {
    if(isCalendar && didComponentMount) {
      handleCalendarRequest();
    }
  }, [initialDate, finalDate, dateCycle]);

  useEffect(() => {
    if(isCalendar && !didComponentMount && !isMonthView) {
      handleCalendarRequest();
    }
  }, [dateCycle]);

  useEffect(() => {
    if(isCalendar && !didComponentMount) {
      handleCalendarRequest();
    }
  }, [qp]);

  useEffect(() => {
    if(!didComponentMount) {
      handleCalendarRequest();
    }
  }, [selectedSchedulingOption]);

  useEffect(() => {
    if(isMap) {
      handleMapRequest();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if(isMap && !didComponentMount) {
      handleMapRequest();
    }
  }, [qp]);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(position => {
      const { latitude, longitude } = position.coords;

      setUserLocation({
        lat: latitude,
        lng: longitude
      });
    });
  }, [mapList]);

  useEffect(() => {
    (async () => {
      const establishmentAddress = `${number ? `${street}, ${number}` : street} - ${district} ${postalCode}, ${city} - ${state}`;
      const [lat, lng] = await getAddressLatLng(establishmentAddress);
      setEstablishmentLocation({ lat, lng });
    })();
  }, [mapList]);

  useEffect(() => {
    if(isMap) {
      if(mapList.length > 0) {
        if(window.document.readyState == 'loading') {
          navigator.geolocation.getCurrentPosition(position => {
            const { latitude, longitude } = position.coords;

            setMapDefaultLocation({
              lat: latitude,
              lng: longitude
            });
          });
        }

        if(window.document.readyState == 'complete') {
          let bounds = new window.google.maps.LatLngBounds();

          mapList.forEach(item => bounds.extend({ lat: item.latitude, lng: item.longitude }));

          const lat = bounds.getCenter().lat();
          const lng = bounds.getCenter().lng();

          setMapDefaultLocation({
            lat,
            lng
          });
        }
      }
    }
  }, [isMap, mapList, window.document.readyState]);

  const services = selects
    ?.find(filter => filter?.id == 'services')
    ?.options
    ?.map(service => {
      return {
        value: service?.serviceId,
        label: service?.description,
        categories: service?.categories?.filter(category => !!category)
      }
    })
    ?.filter(service => !!service?.value);

  const getCalendarValidRange = () => {
    if(isDayView) {
      return {
        start: dateCycle.format('YYYY-MM-DD'),
        end: dateCycle.format('YYYY-MM-DD')
      }
    }

    if(isWeekView) {
      return {
        start: dateCycle.startOf('week').format('YYYY-MM-DD'),
        end: dateCycle.clone().endOf('week').add(1, 'days').format('YYYY-MM-DD')
      }
    }
  }

  const getCalendarValidMonthRange = () => {
    const end = moment(dateCycle, 'YYYY-MM-DD').endOf('month').add(1, 'days').format('YYYY-MM-DD');

    return {
      start: initialDate,
      end
    }
  }

  const handleDateCycleChange = date => {
    if(isDayView) {
      if(date == 'prev') {
        return setDateCycle(moment(dateCycle.subtract(1, 'days')));
      }
  
      if(date == 'next') {
        return setDateCycle(moment(dateCycle.add(1, 'days')));
      }
    }

    if(isWeekView) {
      if(date == 'prev') {
        return setDateCycle(moment(dateCycle.subtract(1, 'week')));
      }
  
      if(date == 'next') {
        return setDateCycle(moment(dateCycle.add(1, 'week')));
      }
    }

    if(isMonthView) {
      if(date == 'prev') {
        const newDateCycle = moment(dateCycle.subtract(1, 'month'));
        setDateCycle(newDateCycle);

        const extraProps = {
          initialDate: newDateCycle.startOf('month').format('YYYY-MM-DD'),
          finalDate: newDateCycle.endOf('month').format('YYYY-MM-DD')
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, qp)).then(() => {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.prev();
        });
      }

      if(date == 'next') {
        const newDateCycle = moment(dateCycle.add(1, 'month'));
        setDateCycle(newDateCycle);

        const extraProps = {
          initialDate: newDateCycle.startOf('month').format('YYYY-MM-DD'),
          finalDate: newDateCycle.endOf('month').format('YYYY-MM-DD')
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, qp)).then(() => {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.next();
        });
      }
    }
  }

  const getDirections = (start, end) => {
    const directions = `https://www.google.com/maps?f=d&dirflg=d&saddr=${start?.lat},${start?.lng}&daddr=${end?.lat},${end?.lng}`;
    return window.open(directions, '_blank');
  }

  const handleFilterSubmit = values => {
    const formattedParams = Object.fromEntries(Object.entries(values).filter(([_, value]) => value));
    const params = `&${qs.stringify(formattedParams)}`;

    setIsFilterActive(true);
    setIsMobileFilterOpen(false);

    dispatch(persistQueryParamsSearch(params));
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      serviceId: '',
      situationId: [],
      typeId: ''
    },
    onSubmit: values => handleFilterSubmit(values)
  });

  return(
    <>
      {isSchedulingMenuOpen && (
        <SchedulingMobileMenu
          isOpen={isSchedulingMenuOpen}
          onClose={() => setIsSchedulingMenuOpen(false)}
          setIsCalendar={setIsCalendar}
          setIsMap={setIsMap}
        />
      )}
      {isAppointmentServicesDrawerOpen && (
        <SchedulingServicesView
          id={appointmentServicesId}
          isOpen={isAppointmentServicesDrawerOpen}
          onClose={() => {
            setAppointmentServicesId('');
            setIsAppointmentServicesDrawerOpen(false);
          }}
        />
      )}
      {isAppointmentOthersDrawerOpen && (
        <SchedulingOthersView
          id={appointmentOthersId}
          isOpen={isAppointmentOthersDrawerOpen}
          onClose={() => {
            setAppointmentOthersId('');
            setIsAppointmentOthersDrawerOpen(false);
          }}
        />
      )}
      {isMobileFilterOpen && (
        <Modal
          id='scheduling-mobile-filters'
          title='Filtrar'
          open={isMobileFilterOpen}
          onClose={() => setIsMobileFilterOpen(false)}
        >
          <form
            onSubmit={formik.handleSubmit}
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <Grid container>
              <Grid item xs={12}>
                <Autocomplete
                  id="serviceOptions"
                  getOptionLabel={option => option.label}
                  style={{ width: '100%' }}
                  classes={autoCompleteStyles}
                  options={services || []}
                  loading={false}
                  value={services?.find(service => +service?.value == +formik.values.serviceId) || {}}
                  loadingText={<p>Carregando...</p>}
                  noOptionsText={<p>Sem opções</p>}
                  onChange={(_, service) => {
                    if(!service) {
                      formik.setFieldValue('serviceId', '');
                    }

                    if(service) {
                      formik.setFieldValue('serviceId', service?.value);
                    }
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Serviço"
                      variant="outlined"
                    />
                  )}
                  renderOption={props => {
                    return(
                      <span {...props}>
                        <Typography color='primary'>{props.label}</Typography>
                        {(props?.categories?.length > 0) && (
                          <Typography color='textSecondary'>Categoria(s): {formatServicesCategories(props?.categories, servicesCategories)}</Typography>
                        )}
                      </span>
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Select
                  multiple
                  name='situationId'
                  label='Situação'
                  options={situationOptions}
                  value={formik.values.situationId}
                  onChange={formik.handleChange}
                />
              </Grid>
              {isCalendar && (
                <Grid item xs={12}>
                  <Select
                    name='typeId'
                    label='Tipo de agendamento'
                    options={appointmentTypeOptions}
                    value={formik.values.typeId}
                    onChange={formik.handleChange}
                  />
                </Grid>
              )}
              <Grid item xs={12} style={{ marginTop: 20 }}>
                <Button
                  fullWidth
                  color='success'
                  type='submit'
                >
                  Filtrar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Modal>
      )}
      {(isSm && isMap) && (
        <>
          {!isFilterActive && (
            <button
              style={{ position: 'fixed', bottom: 20, left: 20, zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#022A5C', width: 45, height: 45, borderRadius: '50%', border: '0 none', cursor: 'pointer' }}
              onClick={() => setIsMobileFilterOpen(true)}
            >
              <FontAwesomeIcon
                icon={faFilter}
                color="#FFFFFF"
                size="lg"
              />
            </button>
          )}
          {isFilterActive && (
            <div style={{ position: 'fixed', bottom: 20, left: 20, zIndex: 100, display: 'flex', alignItems: 'center' }}>
              <button
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F66C6B', width: 45, height: 45, borderRadius: '50%', border: '0 none', cursor: 'pointer' }}
                onClick={() => {
                  formik.resetForm();
                  dispatch(persistQueryParamsSearch(''));
                  setIsFilterActive(false);
                }}
              >
                <FontAwesomeIcon
                  icon={faFilter}
                  color="#FFFFFF"
                  size="lg"
                />
              </button>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F66C6B', width: 110, height: 20, borderTopRightRadius: 15, borderBottomRightRadius: 15, marginLeft: -10 }}>
                <p style={{ color: '#FFFFFF', margin: 0 }}>
                  <b>Limpar filtros</b>
                </p>
              </div>
              <div style={{ position: 'absolute', bottom: -5, left: 25, padding: 2.5, backgroundColor: '#FFFFFF', borderRadius: '50%' }}>
                <IconButton
                  style={{ backgroundColor: '#F66C6B', height: 5, width: 5, padding: 10 }}
                  onClick={() => {
                    formik.resetForm();
                    dispatch(persistQueryParamsSearch(''));
                    setIsFilterActive(false);
                  }}
                >
                  <Close style={{ color: '#FFFFFF', padding: 5 }} />
                </IconButton>
              </div>
            </div>
          )}
        </>
      )}
      {isSm && (
        <SchedulingAddMobile isMap={isMap} />
      )}
      {(isSm && isMap) && (
        <IconButton
          style={{ position: 'fixed', bottom: 20, left: '45%', zIndex: 800, backgroundColor: '#022A5C' }}
          onClick={() => setIsSchedulingMenuOpen(true)}
        >
          <Menu style={{ color: '#FFFFFF' }} />
        </IconButton>
      )}
      {!isSm && (
        <Paper>
          <div style={{ display: 'flex', flexDirection: isSm ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'center', padding: 10 }}>
            <div style={{ width: isSm ? '100%' : '20%' }}>
              <div style={{ width: 'fit-content' }}>
                <Dropdown
                  hasDropDownIcon={false}
                  color='success'
                  variant='contained'
                  label={
                    <>
                      <FontAwesomeIcon
                        icon={faPlus}
                        color="#FFFFFF"
                        size="md"
                        style={{ marginRight: 5 }}
                      />
                      Novo agendamento
                    </>
                  }
                >
                  <Button
                    fullWidth
                    disableElevation
                    color='inherit'
                    onClick={() => history.push('/novo-agendamento-servico')}
                  >
                    Serviço
                  </Button>
                  <Button
                    fullWidth
                    disableElevation
                    color='inherit'
                    onClick={() => history.push('/novo-agendamento')}
                  >
                    Outros
                  </Button>
                </Dropdown>
              </div>
            </div>
            {isSm && (
              <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 5, marginTop: 5 }}>
                {/* <div style={{ width: 1, height: 40, backgroundColor: '#D7D7D7', marginLeft: 10 }} />
                <IconButton onClick={() => history.push('/configuracao-agendamento')}>
                  <FontAwesomeIcon
                    icon={faCog}
                    color="#757575"
                    size="md"
                  />
                </IconButton> */}
              </div>
            )}
            <div style={{ width: isSm ? '100%' : '55%' }}>
              <form
                onSubmit={formik.handleSubmit}
                style={{ display: 'flex', flexDirection: width < 700 ? 'column' : 'row', alignItems: 'center', gap: 5 }}
              >
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Autocomplete
                    id="serviceOptions"
                    getOptionLabel={option => option.label}
                    style={{ width: '100%' }}
                    classes={autoCompleteStyles}
                    options={services || []}
                    loading={false}
                    value={services?.find(service => +service?.value == +formik.values.serviceId) || {}}
                    loadingText={<p>Carregando...</p>}
                    noOptionsText={<p>Sem opções</p>}
                    onChange={(_, service) => {
                      if(!service) {
                        formik.setFieldValue('serviceId', '');
                      }

                      if(service) {
                        formik.setFieldValue('serviceId', service?.value);
                      }
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Serviço"
                        variant="outlined"
                      />
                    )}
                    renderOption={props => {
                      return(
                        <span {...props}>
                          <Typography color='primary'>{props.label}</Typography>
                          {(props?.categories?.length > 0) && (
                            <Typography color='textSecondary'>Categoria(s): {formatServicesCategories(props?.categories, servicesCategories)}</Typography>
                          )}
                        </span>
                      );
                    }}
                  />
                  <Select
                    multiple
                    name='situationId'
                    label='Situação'
                    options={situationOptions}
                    value={formik.values.situationId}
                    onChange={formik.handleChange}
                  />
                  {isCalendar && (
                    <Select
                      name='typeId'
                      label='Tipo de agendamento'
                      options={appointmentTypeOptions}
                      value={formik.values.typeId}
                      onChange={formik.handleChange}
                    />
                  )}
                </div>
                <Button
                  fullWidth={width < 700}
                  color='primary'
                  type='submit'
                >
                  Filtrar
                </Button>
                {isFilterActive && (
                  <IconButton
                    type='button'
                    onClick={() => {
                      setIsFilterActive(false);
                      formik.resetForm();
                      dispatch(persistQueryParamsSearch(''));
                    }}
                  >
                    <Close />
                  </IconButton>
                )}
              </form>
            </div>
            {!isSm && (
              <div style={{ width: '25%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 5, padding: 5 }}>
                {/* <div style={{ width: 1, height: 40, backgroundColor: '#D7D7D7', marginLeft: 10 }} />
                <IconButton onClick={() => history.push('/configuracao-agendamento')}>
                  <FontAwesomeIcon
                    icon={faCog}
                    color="#757575"
                    size="md"
                  />
                </IconButton> */}
              </div>
            )}
          </div>
        </Paper>
      )}
      {isCalendar && (
        <Paper style={{ marginTop: 10 }}>
          <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}>
            <Map
              style={{ height: 0, width: 0 }}
              mapId='scheduling-map'
              disableDefaultUI
              defaultZoom={14}
              defaultCenter={mapDefaultLocation}
            />
          </APIProvider>
          <Grid container>
            <Grid item xs={12} style={{ padding: 10 }}>
              <Loader isLoading={isLoading && !isMonthView}>
                {(isSm && !isLoading) && (
                  <Grid container>
                    <Grid xs={12} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', backgroundColor: '#EFEFEF' }}>
                      <IconButton
                        onClick={() => {
                          setIsCalendar(false);
                          setIsMap(true);
                        }}
                      >
                        <MapIcon />
                      </IconButton>
                      {!isFilterActive && (
                        <IconButton onClick={() => setIsMobileFilterOpen(true)}>
                          <FontAwesomeIcon
                            icon={faFilter}
                            color="#757575"
                            size="sm"
                          />
                        </IconButton>
                      )}
                      {isFilterActive && (
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', margin: 10 }}>
                          <button
                            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F66C6B', width: 45, height: 45, borderRadius: '50%', border: '0 none', cursor: 'pointer' }}
                            onClick={() => {
                              formik.resetForm();
                              dispatch(persistQueryParamsSearch(''));
                              setIsFilterActive(false);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faFilter}
                              color="#FFFFFF"
                              size="lg"
                            />
                          </button>
                          <div style={{ position: 'absolute', bottom: -5, left: 25, padding: 2.5, backgroundColor: '#FFFFFF', borderRadius: '50%' }}>
                            <IconButton
                              style={{ backgroundColor: '#F66C6B', height: 5, width: 5, padding: 10 }}
                              onClick={() => {
                                formik.resetForm();
                                dispatch(persistQueryParamsSearch(''));
                                setIsFilterActive(false);
                              }}
                            >
                              <Close style={{ color: '#FFFFFF', padding: 5 }} />
                            </IconButton>
                          </div>
                        </div>
                      )}
                    </Grid>
                  </Grid>
                )}
                {(isLoading && isMonthView) && (
                  <Grid container xs={12} justify='center'>
                    <CircularProgress size={30} />
                  </Grid>
                )}
                {(selectedSchedulingOption == 'dayGridMonth') && (
                  <div style={{ width: isLoading ? 0 : '100%' }}>
                    <FullCalendar
                      ref={calendarRef}
                      locale='pt-br'
                      plugins={[ interactionPlugin, dayGridPlugin, timeGridPlugin ]}
                      headerToolbar={{
                        start: '',
                        center: '',
                        end: ''
                      }}
                      dayHeaderContent={option => {
                        if(isSm) {
                          if(isMonthView) {
                            return(
                              <Typography color='textSecondary' variant='body2'>
                                <b>{moment(option.date).add(1, 'day').format('ddd').at(0).toUpperCase()}</b>
                              </Typography>
                            );
                          }

                          if(isDayView) {
                            return(
                              <Chip
                                style={{ height: 20, backgroundColor: '#13BC4B', marginBottom: 10 }}
                                label={
                                  <Typography color='primary' variant='body2'>
                                    <b>{capitalizeString(moment(option.date).format('dddd'))}</b>
                                  </Typography>
                                }
                              />
                            );
                          }

                          return(
                            <div>
                              <Typography color={option.isToday ? 'primary' : 'textSecondary'} variant='body2'>
                                <b>{moment(option.date).format('ddd').at(0).toUpperCase()}</b>
                              </Typography>
                              {option.isToday ? (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 25, height: 25, borderRadius: '50%', backgroundColor: '#13BC4B' }}>
                                  <Typography color='primary' variant='body2'>
                                    <b>{moment(option.date).format('D').toUpperCase()}</b>
                                  </Typography>
                                </div>
                              ) : (
                                <Typography color='primary' variant='body2'>
                                  <b>{moment(option.date).format('D').toUpperCase()}</b>
                                </Typography>
                              )}
                            </div>
                          );
                        }

                        if(!isMonthView) {
                          return(
                            <div>
                              {option.isToday ? (
                                <Chip
                                  style={{ height: 20, backgroundColor: '#13BC4B' }}
                                  label={
                                    <Typography style={{ color: '#FFFFFF' }}>
                                      {moment(option.date).format('DD/MMM')}
                                    </Typography>
                                  }
                                />
                              ) : (
                                <Typography color='primary'>
                                  {moment(option.date).format('DD/MMM')}
                                </Typography>
                              )}
                              <Typography color='textSecondary' variant='body2'>
                                {moment(option.date).format('ddd').toUpperCase()}
                              </Typography>
                            </div>
                          );
                        }

                        return(
                          <div>
                            <Typography color='textSecondary' variant='body2'>
                              {capitalizeString(moment(option.date).add(1, 'day').format('ddd'))}
                            </Typography>
                          </div>
                        );
                      }}
                      dayCellContent={option => {
                        if(isMonthView) {
                          if(isSm) {
                            return(
                              <Typography color='primary' style={{ textAlign: 'center' }}>
                                <b>{moment(option.date).format('D')}</b>
                              </Typography>
                            );
                          }

                          return(
                            <div>
                              {option.isToday ? (
                                <div style={{ textAlign: 'end' }}>
                                  <Chip
                                    style={{ height: 20, backgroundColor: '#13BC4B' }}
                                    label={
                                      <Typography style={{ color: '#FFFFFF' }}>
                                        {moment(option.date).format('DD')}
                                      </Typography>
                                    }
                                  />
                                </div>
                              ) : (
                                <div style={{ textAlign: 'end' }}>
                                  <Typography color='primary'>
                                    {moment(option.date).format('DD')}
                                  </Typography>
                                </div>
                              )}
                            </div>
                          );
                        }
                      }}
                      initialView='dayGridMonth'
                      validRange={{}}
                      firstDay={1}
                      allDaySlot={false}
                      expandRows={true}
                      fixedWeekCount={false}
                      height={isLoading ? 0 : '100vh'}
                      aspectRatio={isLoading ? 0 : 'inherit'}
                      events={calendarList}
                      eventContent={renderEventContent}
                      eventClick={({ event }) => {
                        const { type } = event._def.extendedProps;
                        const { publicId } = event._def;

                        if(+type == 1) {
                          setAppointmentServicesId(publicId);
                          setIsAppointmentServicesDrawerOpen(true);
                        }

                        if(+type == 2) {
                          setAppointmentOthersId(publicId);
                          setIsAppointmentOthersDrawerOpen(true);
                        }
                      }}
                    />
                  </div>
                )}
                {(selectedSchedulingOption == 'timeGridWeek') && (
                  <FullCalendar
                    ref={calendarRef}
                    locale='pt-br'
                    plugins={[ interactionPlugin, dayGridPlugin, timeGridPlugin ]}
                    headerToolbar={{
                      start: '',
                      center: '',
                      end: ''
                    }}
                    dayHeaderContent={option => {
                      if(isSm) {
                        if(isMonthView) {
                          return(
                            <Typography color='textSecondary' variant='body2'>
                              <b>{moment(option.date).add(1, 'day').format('ddd').at(0).toUpperCase()}</b>
                            </Typography>
                          );
                        }

                        if(isDayView) {
                          return(
                            <Chip
                              style={{ height: 20, backgroundColor: '#13BC4B', marginBottom: 10 }}
                              label={
                                <Typography color='primary' variant='body2'>
                                  <b>{capitalizeString(moment(option.date).format('dddd'))}</b>
                                </Typography>
                              }
                            />
                          );
                        }

                        return(
                          <div>
                            <Typography color={option.isToday ? 'primary' : 'textSecondary'} variant='body2'>
                              <b>{moment(option.date).format('ddd').at(0).toUpperCase()}</b>
                            </Typography>
                            {option.isToday ? (
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 25, height: 25, borderRadius: '50%', backgroundColor: '#13BC4B' }}>
                                <Typography color='primary' variant='body2'>
                                  <b>{moment(option.date).format('D').toUpperCase()}</b>
                                </Typography>
                              </div>
                            ) : (
                              <Typography color='primary' variant='body2'>
                                <b>{moment(option.date).format('D').toUpperCase()}</b>
                              </Typography>
                            )}
                          </div>
                        );
                      }

                      if(!isMonthView) {
                        return(
                          <div>
                            {option.isToday ? (
                              <Chip
                                style={{ height: 20, backgroundColor: '#13BC4B' }}
                                label={
                                  <Typography style={{ color: '#FFFFFF' }}>
                                    {moment(option.date).format('DD/MMM')}
                                  </Typography>
                                }
                              />
                            ) : (
                              <Typography color='primary'>
                                {moment(option.date).format('DD/MMM')}
                              </Typography>
                            )}
                            <Typography color='textSecondary' variant='body2'>
                              {moment(option.date).format('ddd').toUpperCase()}
                            </Typography>
                          </div>
                        );
                      }

                      return(
                        <div>
                          <Typography color='textSecondary' variant='body2'>
                            {capitalizeString(moment(option.date).add(1, 'day').format('ddd'))}
                          </Typography>
                        </div>
                      );
                    }}
                    dayCellContent={option => {
                      if(isMonthView) {
                        if(isSm) {
                          return(
                            <Typography color='primary' style={{ textAlign: 'center' }}>
                              <b>{moment(option.date).format('D')}</b>
                            </Typography>
                          );
                        }

                        return(
                          <div>
                            {option.isToday ? (
                              <div style={{ textAlign: 'end' }}>
                                <Chip
                                  style={{ height: 20, backgroundColor: '#13BC4B' }}
                                  label={
                                    <Typography style={{ color: '#FFFFFF' }}>
                                      {moment(option.date).format('DD')}
                                    </Typography>
                                  }
                                />
                              </div>
                            ) : (
                              <div style={{ textAlign: 'end' }}>
                                <Typography color='primary'>
                                  {moment(option.date).format('DD')}
                                </Typography>
                              </div>
                            )}
                          </div>
                        );
                      }
                    }}
                    initialView='timeGridWeek'
                    validRange={getCalendarValidRange}
                    firstDay={null}
                    allDaySlot={false}
                    expandRows={true}
                    fixedWeekCount={false}
                    slotMinTime='04:00:00'
                    slotMaxTime='23:59:59'
                    height={isLoading ? 0 : '100vh'}
                    contentHeight={isLoading ? 0 : '100vh'}
                    events={calendarList}
                    eventContent={renderEventContent}
                    eventClick={({ event }) => {
                      const { type } = event._def.extendedProps;
                      const { publicId } = event._def;

                      if(+type == 1) {
                        setAppointmentServicesId(publicId);
                        setIsAppointmentServicesDrawerOpen(true);
                      }

                      if(+type == 2) {
                        setAppointmentOthersId(publicId);
                        setIsAppointmentOthersDrawerOpen(true);
                      }
                    }}
                  />
                )}
                {(selectedSchedulingOption == 'timeGridDay') && (
                  <FullCalendar
                    ref={calendarRef}
                    locale='pt-br'
                    plugins={[ interactionPlugin, dayGridPlugin, timeGridPlugin ]}
                    headerToolbar={{
                      start: '',
                      center: '',
                      end: ''
                    }}
                    dayHeaderContent={option => {
                      if(isSm) {
                        if(isMonthView) {
                          return(
                            <Typography color='textSecondary' variant='body2'>
                              <b>{moment(option.date).add(1, 'day').format('ddd').at(0).toUpperCase()}</b>
                            </Typography>
                          );
                        }

                        if(isDayView) {
                          return(
                            <Chip
                              style={{ height: 20, backgroundColor: '#13BC4B', marginBottom: 10 }}
                              label={
                                <Typography color='primary' variant='body2'>
                                  <b>{capitalizeString(moment(option.date).format('dddd'))}</b>
                                </Typography>
                              }
                            />
                          );
                        }

                        return(
                          <div>
                            <Typography color={option.isToday ? 'primary' : 'textSecondary'} variant='body2'>
                              <b>{moment(option.date).format('ddd').at(0).toUpperCase()}</b>
                            </Typography>
                            {option.isToday ? (
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 25, height: 25, borderRadius: '50%', backgroundColor: '#13BC4B' }}>
                                <Typography color='primary' variant='body2'>
                                  <b>{moment(option.date).format('D').toUpperCase()}</b>
                                </Typography>
                              </div>
                            ) : (
                              <Typography color='primary' variant='body2'>
                                <b>{moment(option.date).format('D').toUpperCase()}</b>
                              </Typography>
                            )}
                          </div>
                        );
                      }

                      if(!isMonthView) {
                        return(
                          <div>
                            {option.isToday ? (
                              <Chip
                                style={{ height: 20, backgroundColor: '#13BC4B' }}
                                label={
                                  <Typography style={{ color: '#FFFFFF' }}>
                                    {moment(option.date).format('DD/MMM')}
                                  </Typography>
                                }
                              />
                            ) : (
                              <Typography color='primary'>
                                {moment(option.date).format('DD/MMM')}
                              </Typography>
                            )}
                            <Typography color='textSecondary' variant='body2'>
                              {moment(option.date).format('ddd').toUpperCase()}
                            </Typography>
                          </div>
                        );
                      }

                      return(
                        <div>
                          <Typography color='textSecondary' variant='body2'>
                            {capitalizeString(moment(option.date).add(1, 'day').format('ddd'))}
                          </Typography>
                        </div>
                      );
                    }}
                    dayCellContent={option => {
                      if(isMonthView) {
                        if(isSm) {
                          return(
                            <Typography color='primary' style={{ textAlign: 'center' }}>
                              <b>{moment(option.date).format('D')}</b>
                            </Typography>
                          );
                        }

                        return(
                          <div>
                            {option.isToday ? (
                              <div style={{ textAlign: 'end' }}>
                                <Chip
                                  style={{ height: 20, backgroundColor: '#13BC4B' }}
                                  label={
                                    <Typography style={{ color: '#FFFFFF' }}>
                                      {moment(option.date).format('DD')}
                                    </Typography>
                                  }
                                />
                              </div>
                            ) : (
                              <div style={{ textAlign: 'end' }}>
                                <Typography color='primary'>
                                  {moment(option.date).format('DD')}
                                </Typography>
                              </div>
                            )}
                          </div>
                        );
                      }
                    }}
                    initialView='timeGridDay'
                    validRange={getCalendarValidRange}
                    firstDay={null}
                    allDaySlot={false}
                    expandRows={true}
                    fixedWeekCount={false}
                    slotMinTime='04:00:00'
                    slotMaxTime='23:59:59'
                    height={isLoading ? 0 : '100vh'}
                    contentHeight={isLoading ? 0 : '100vh'}
                    events={calendarList}
                    eventContent={renderEventContent}
                    eventClick={({ event }) => {
                      const { type } = event._def.extendedProps;
                      const { publicId } = event._def;

                      if(+type == 1) {
                        setAppointmentServicesId(publicId);
                        setIsAppointmentServicesDrawerOpen(true);
                      }

                      if(+type == 2) {
                        setAppointmentOthersId(publicId);
                        setIsAppointmentOthersDrawerOpen(true);
                      }
                    }}
                  />
                )}
              </Loader>
            </Grid>
          </Grid>
        </Paper>
      )}
      {isMap && (
        <Paper style={{ marginTop: 10 }}>
          <Grid container>
            <Grid item xs={12} style={{ height: isSm ? '95vh' : '70vh' }}>
              <Loader isLoading={isLoading} style={{ height: '100%' }}>
                <SchedulingMapEventView
                  activeId={activeMapListId}
                  getDirections={getDirections}
                  userLocation={userLocation}
                />
                <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}>
                  {(window.document.readyState == 'complete') && (
                    <Map
                      mapId='scheduling-map'
                      disableDefaultUI
                      minZoom={5}
                      defaultZoom={12}
                      defaultCenter={mapList?.length > 0 ? mapDefaultLocation : userLocation}
                    >
                      <MapMarkerWithInfoWindow
                        key='no-scheduling-map'
                        isOpen={isNoneSchedulingModalOpen && !isEstablishmentLoading}
                        setIsOpen={setIsNoneSchedulingModalOpen}
                        services={[]}
                        situationId={6}
                        position={{
                          lat: establishmentLocation.lat || userLocation.lat,
                          lng: establishmentLocation.lng || userLocation.lng
                        }}
                        noSchedulingLayout={
                          <div>
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', borderBottom: (mapList.length == 0) ? '1px solid #D7D7D7' : 'none' }}>
                              {(mapList.length == 0) && (
                                <>
                                  <EventBusy style={{ color: '#F66C6B', height: 50, width: 50 }} />
                                  <Typography>
                                    <b>Você não possui nenhum agendamento <br /> para visualizar.</b>
                                  </Typography>
                                </>
                              )}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: 10 }}>
                              <img src={IconMapPin} />
                              <div>
                                <Typography color='primary'>
                                  <b>{establishmentName}</b>
                                </Typography>
                                <Typography color='textSecondary'>
                                  <b>{number ? `${street}, ${number}` : street} - {district} {postalCode} <br /> {city} - {state}</b>
                                </Typography>
                              </div>
                            </div>
                          </div>
                        }
                      />
                      {mapList.map(item => {
                        if(+item.latitude != 0 && +item.longitude != 0) {
                          return(
                            <MapMarkerWithInfoWindow
                              key={item?.id}
                              services={item?.services}
                              address={item?.completeAddress}
                              startDateTime={item?.startDateTime}
                              endDateTime={item?.endDateTime}
                              situationId={item?.situationId}
                              isOpen={activeMapListId == item.id}
                              getDirections={() => getDirections(userLocation, { lat: item?.latitude, lng: item?.longitude })}
                              onClick={() => {
                                if(activeMapListId == item?.id) {
                                  return setActiveMapListId('');
                                }

                                setActiveMapListId(item?.id);
                              }}
                              position={{
                                lat: item?.latitude,
                                lng: item?.longitude
                              }}
                            />
                          );
                        }
                      })}
                    </Map>
                  )}
                </APIProvider>
              </Loader>
            </Grid>
          </Grid>
        </Paper>
      )}
    </>
  );
}